// src/pages/PressKit.js - Redesigned to match main design
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, FileText } from "lucide-react";
import SEO from '../components/SEO';

/* ═══════════════════════════════════════════════════════════════════
   GLOBAL STYLES - Matching Home.js design system
══════════════════════════════════════════════════════════════════ */
const PageStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@700;900&display=swap');
    @font-face { font-family:'Azonix'; src:url('/fonts/Azonix.otf') format('opentype'),url('/fonts/azonix.woff2') format('woff2'); font-display:swap; }
    @font-face { font-family:'Japanese3017'; src:url('/fonts/Japanese-3017.ttf') format('truetype'); font-display:swap; }
    @font-face { font-family:'Acknowledgement'; src:url('/fonts/Acknowledgement.otf') format('opentype'); font-display:swap; }

    :root {
      --az: 'Azonix','Impact',sans-serif;
      --jp: 'Japanese3017','Noto Serif JP',serif;
      --wt: 'Acknowledgement','Azonix',sans-serif;
      --ui: -apple-system,'Helvetica Neue',sans-serif;
      --bd: 'Georgia',serif;
      --neu: #a78bfa;
      --bg: #08080c;
      --glass: rgba(255,255,255,0.04);
      --gb: rgba(255,255,255,0.08);
    }

    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
    html{scroll-behavior:smooth;}

    .press-root {
      background: var(--bg);
      color: #fff;
      font-family: var(--ui);
      overflow-x: hidden;
      min-height: 100vh;
      position: relative;
    }

    .press-wrap {
      max-width: 1240px;
      margin: 0 auto;
      padding: 0 clamp(16px,4vw,52px);
      position: relative;
      z-index: 1;
    }
    @media(max-width:480px) {
      .press-wrap {
        padding: 0 16px;
      }
    }

    .press-sec { padding: clamp(56px,8vh,100px) 0; }
    .press-sec-sm { padding: clamp(36px,5vh,64px) 0; }
    @media(max-width:480px) {
      .press-sec { padding: clamp(32px,6vh,48px) 0; }
      .press-sec-sm { padding: clamp(24px,4vh,36px) 0; }
    }

    /* Section Header */
    .press-sh {
      display: flex;
      align-items: center;
      margin-bottom: 28px;
    }
    @media(max-width:480px) {
      .press-sh {
        margin-bottom: 20px;
      }
    }
    .press-sh-left { display:flex; align-items:center; gap:12px; }
    @media(max-width:480px) {
      .press-sh-left { gap:10px; }
    }
    .press-sh-bar { width:3px; height:26px; border-radius:2px; flex-shrink:0; }
    .press-sh-label {
      font-family: var(--az);
      font-size: 16px;
      letter-spacing: 1px;
      text-transform: uppercase;
    }
    @media(max-width:480px) {
      .press-sh-label {
        font-size: 13px;
      }
    }

    /* Glass Card */
    .press-card {
      background: var(--glass);
      backdrop-filter: blur(24px);
      border: 1px solid var(--gb);
      border-radius: 18px;
      padding: clamp(24px,3vw,36px);
      transition: all .3s ease;
    }
    .press-card:hover {
      background: rgba(255,255,255,0.07);
      border-color: rgba(167,139,250,0.25);
    }

    /* Asset Grid */
    .press-assets-grid {
      display: grid;
      grid-template-columns: repeat(2,1fr);
      gap: 20px;
    }
    @media(max-width:768px){ 
      .press-assets-grid { grid-template-columns: 1fr; } 
    }

    .press-asset-card {
      background: var(--glass);
      border: 1px solid var(--gb);
      border-radius: 18px;
      padding: 28px;
      transition: all .35s ease;
      display: flex;
      align-items: flex-start;
      gap: 20px;
    }
    @media(max-width:480px) {
      .press-asset-card {
        flex-direction: column;
        align-items: stretch;
        padding: 20px;
        gap: 16px;
      }
    }
    .press-asset-card:hover {
      background: rgba(255,255,255,0.07);
      border-color: rgba(167,139,250,0.3);
      transform: translateY(-4px);
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
    }
    @media(max-width:480px) {
      .press-asset-card:hover {
        transform: none;
      }
    }
    .press-asset-card:hover {
      background: rgba(255,255,255,0.07);
      border-color: rgba(167,139,250,0.3);
      transform: translateY(-4px);
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
    }
    .press-asset-icon {
      width: 56px; height: 56px;
      border-radius: 14px;
      background: linear-gradient(135deg, rgba(167,139,250,0.15), rgba(167,139,250,0.05));
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .press-asset-content {
      flex: 1;
    }
    .press-asset-title {
      font-family: var(--az);
      font-size: 16px;
      color: #fff;
      margin-bottom: 4px;
    }
    @media(max-width:480px) {
      .press-asset-title {
        font-size: 14px;
      }
    }
    .press-asset-subtitle {
      font-size: 12px;
      color: rgba(255,255,255,0.38);
      margin-bottom: 8px;
    }
    .press-asset-desc {
      font-size: 13px;
      color: rgba(255,255,255,0.5);
      line-height: 1.5;
    }
    @media(max-width:480px) {
      .press-asset-desc {
        font-size: 12px;
      }
    }
    .press-asset-download {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 10px 18px;
      border-radius: 8px;
      background: rgba(167,139,250,0.1);
      border: 1px solid rgba(167,139,250,0.25);
      color: var(--neu);
      font-family: var(--az);
      font-size: 9px;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      text-decoration: none;
      transition: all .25s;
      flex-shrink: 0;
    }
    @media(max-width:480px) {
      .press-asset-download {
        width: 100%;
        justify-content: center;
        padding: 12px 16px;
      }
    }
    .press-asset-download:hover {
      background: rgba(167,139,250,0.2);
      transform: translateY(-2px);
    }

    /* Quote Section */
    .press-quote {
      text-align: center;
      padding: clamp(36px,5vw,56px);
      background: linear-gradient(135deg, rgba(59,130,246,0.06) 0%, rgba(167,139,250,0.04) 100%);
      border: 1px solid rgba(255,255,255,0.06);
      border-radius: 22px;
      position: relative;
      overflow: hidden;
    }
    @media(max-width:480px) {
      .press-quote {
        padding: clamp(24px,4vw,32px);
        border-radius: 16px;
      }
    }
    .press-quote-glow {
      position: absolute;
      top: 50%; left: 50%;
      transform: translate(-50%,-50%);
      width: 400px; height: 160px;
      border-radius: 50%;
      background: radial-gradient(ellipse, rgba(59,130,246,0.08), transparent);
      filter: blur(40px);
      pointer-events: none;
    }
    .press-quote-icon {
      position: relative;
      z-index: 1;
      margin-bottom: 16px;
    }
    .press-quote-text {
      position: relative;
      z-index: 1;
      font-size: clamp(14px,1.5vw,18px);
      font-style: italic;
      color: rgba(255,255,255,0.8);
      font-family: var(--bd);
      line-height: 1.8;
      max-width: 600px;
      margin: 0 auto 12px;
    }
    @media(max-width:480px) {
      .press-quote-text {
        font-size: 14px;
        line-height: 1.7;
      }
    }
    .press-quote-author {
      position: relative;
      z-index: 1;
      font-family: var(--az);
      font-size: 11px;
      letter-spacing: 2px;
      text-transform: uppercase;
      color: #3b82f6;
    }

    /* Hero */
    .press-hero {
      position: relative;
      min-height: 45vh;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }
    @media(max-width:768px) {
      .press-hero {
        min-height: 35vh;
        padding: 20px;
      }
    }
    @media(max-width:480px) {
      .press-hero {
        min-height: 30vh;
      }
    }
    .press-hero-bg {
      position: absolute;
      inset: 0;
      background-image: url('https://images.unsplash.com/photo-1535016120720-40c646be5580?w=1920&q=80');
      background-size: cover;
      background-position: center;
    }
    .press-hero-overlay {
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, rgba(8,8,12,0.92) 0%, rgba(8,8,12,0.65) 50%, rgba(8,8,12,0.78) 100%);
    }
    .press-hero-glow1 {
      position: absolute;
      top: 20%; left: 15%;
      width: 280px; height: 280px;
      border-radius: 50%;
      background: radial-gradient(ellipse, rgba(59,130,246,0.15), transparent);
      filter: blur(60px);
    }
    .press-hero-glow2 {
      position: absolute;
      bottom: 20%; right: 15%;
      width: 220px; height: 220px;
      border-radius: 50%;
      background: radial-gradient(ellipse, rgba(167,139,250,0.12), transparent);
      filter: blur(50px);
    }
    .press-hero-content {
      position: relative;
      z-index: 1;
      text-align: center;
      padding: 80px 20px 50px;
    }
    @media(max-width:480px) {
      .press-hero-content {
        padding: 60px 16px 32px;
      }
    }
    .press-hero-title {
      font-family: var(--az);
      font-size: clamp(36px,6vw,58px);
      letter-spacing: -1px;
      margin-bottom: 16px;
      color: #3b82f6;
    }
    .press-hero-desc {
      font-size: clamp(14px,1.5vw,17px);
      color: rgba(255,255,255,0.42);
      max-width: 520px;
      margin: 0 auto;
      line-height: 1.8;
    }

    /* Divider */
    .press-div {
      height: 1px;
      background: linear-gradient(to right, transparent, rgba(255,255,255,0.06), transparent);
      margin: 0 0 clamp(48px,7vh,80px);
    }
    @media(max-width:480px) {
      .press-div {
        margin: 0 0 clamp(32px,5vh,48px);
      }
    }

    /* Slideshow */
    .press-slideshow {
      max-width: 340px;
      margin: 0 auto 40px;
    }
    @media(min-width:640px) {
      .press-slideshow {
        max-width: 280px;
        float: right;
        margin-left: 32px;
        margin-bottom: 24px;
      }
    }
    @media(max-width:480px) {
      .press-slideshow {
        max-width: 100%;
        margin-bottom: 32px;
      }
    }
    .press-slideshow-container {
      position: relative;
      aspect-ratio: 2/3;
      border-radius: 16px;
      overflow: hidden;
    }
    @media(max-width:480px) {
      .press-slideshow-container {
        aspect-ratio: 3/4;
      }
    }
    .press-slideshow-img {
      position: absolute;
      inset: 0;
      background-size: cover;
      background-position: center;
    }
    .press-slideshow-grad {
      position: absolute;
      inset: 0;
      background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 50%);
    }
    .press-slideshow-dots {
      position: absolute;
      bottom: 16px;
      left: 0;
      right: 0;
      display: flex;
      justify-content: center;
      gap: 8px;
    }
    .press-slideshow-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: rgba(255,255,255,0.3);
      border: none;
      cursor: pointer;
      transition: all .3s ease;
    }
    .press-slideshow-dot.active {
      width: 24px;
      border-radius: 4px;
      background: var(--neu);
    }
    .press-slideshow-dot:not(.active):hover {
      background: rgba(255,255,255,0.5);
    }
  `}</style>
);

/* ═══════════════════════════════════════════════════════════════════
   PARTICLE BACKGROUND
══════════════════════════════════════════════════════════════════ */
const ParticleBackground = () => {
  const ref = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });
  const raf = useRef(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    let W = cv.width = window.innerWidth;
    let H = cv.height = window.innerHeight;

    const resize = () => { W = cv.width = window.innerWidth; H = cv.height = window.innerHeight; };
    window.addEventListener('resize', resize);

    const pts = Array.from({ length: 35 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - .5) * .2, vy: (Math.random() - .5) * .2,
      r: Math.random() * 1.2 + .3, a: Math.random() * .35 + .07,
      h: [220, 250, 270][Math.floor(Math.random() * 3)],
    }));

    const mm = (e) => { mouse.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener('mousemove', mm);

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      const { x: mx, y: my } = mouse.current;
      for (let i = 0; i < pts.length; i++)
        for (let j = i + 1; j < pts.length; j++) {
          const d = Math.hypot(pts[i].x - pts[j].x, pts[i].y - pts[j].y);
          if (d < 100) { ctx.beginPath(); ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y); ctx.strokeStyle = `rgba(59,130,246,${.05 * (1 - d / 100)})`; ctx.lineWidth = .4; ctx.stroke(); }
        }
      pts.forEach(p => {
        const d = Math.hypot(mx - p.x, my - p.y);
        if (d < 90) { p.vx -= (mx - p.x) / d * .028; p.vy -= (my - p.y) / d * .028; }
        p.vx *= .99; p.vy *= .99; p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.h},72%,70%,${p.a})`; ctx.fill();
      });
      const g = ctx.createRadialGradient(mx, my, 0, mx, my, 160);
      g.addColorStop(0, 'rgba(59,130,246,0.04)'); g.addColorStop(1, 'transparent');
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      raf.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { window.removeEventListener('resize', resize); window.removeEventListener('mousemove', mm); cancelAnimationFrame(raf.current); };
  }, []);

  return <canvas ref={ref} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: .5 }} />;
};

/* ═══════════════════════════════════════════════════════════════════
   SLIDESHOW COMPONENT
══════════════════════════════════════════════════════════════════ */
const PortraitSlideshow = () => {
  const slides = [
    { src: 'https://i.pinimg.com/1200x/11/ce/f5/11cef5593997078db2b6ee9b21061581.jpg', alt: 'Anime trends', link: '/outrange/east' },
    { src: 'https://i.pinimg.com/736x/7a/6e/1b/7a6e1b6e54c60109935ac235f10055dc.jpg', alt: 'Western blockbusters', link: '/outrange/west' },
    { src: 'https://i.pinimg.com/1200x/17/cf/72/17cf72663ad6e3d1c23db253b01fe6dd.jpg', alt: 'Cartoon behind scenes', link: '/outrange/west' },
  ];

  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  return (
    <div className="press-slideshow">
      <div className="press-slideshow-container">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={current}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="press-slideshow-img"
            style={{ backgroundImage: `url(${slides[current].src})` }}
          />
        </AnimatePresence>
        <div className="press-slideshow-grad" />
        <div className="press-slideshow-dots">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrent(idx)}
              className={`press-slideshow-dot ${idx === current ? 'active' : ''}`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════ */
export default function PressKit() {
  const assets = [
    { 
      title: "ANIMAC Brand Guidelines", 
      subtitle: "Rules for consistent branding",
      description: "A guide to using ANIMAC's colors, typography, and visuals correctly.",
      file: "/assets/ANIMAC-BrandGuidelines.pdf",
      icon: FileText
    },
    { 
      title: "ANIMAC Logos Pack", 
      subtitle: "Official ANIMAC logos",
      description: "Multiple logo formats for digital, print, and branding use.",
      file: "/assets/ANIMAC-Logos.zip",
      icon: Download
    },
    { 
      title: "ANIMAC Social Media Kit", 
      subtitle: "Ready-to-use posts & assets",
      description: "A collection of templates and graphics to boost ANIMAC's presence across all platforms.",
      file: "/assets/ANIMAC-SocialKit.zip",
      icon: FileText
    },
    { 
      title: "ANIMAC Press Images", 
      subtitle: "High-quality media shots",
      description: "Official images for press, articles, and features on ANIMAC.",
      file: "/assets/ANIMAC-PressImages.zip",
      icon: Download
    },
  ];

  return (
    <>
      <PageStyles />
      <SEO
        title="Press Kit - ANIMAC Brand Assets"
        description="Download ANIMAC brand assets, logos, and media resources for press and media coverage."
        url="/press-kit"
        image="/assets/animac-preview-logo.svg"
        type="website"
      />

      <ParticleBackground />

      <div className="press-root">
        {/* Hero */}
        <div className="press-hero">
          <div className="press-hero-bg" />
          <div className="press-hero-overlay" />
          <div className="press-hero-glow1" />
          <div className="press-hero-glow2" />
          <div className="press-hero-content">
            <motion.h1
              className="press-hero-title"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              Press Kit
            </motion.h1>
            <motion.p
              className="press-hero-desc"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Download ANIMAC brand assets, logos, and media resources for press and media coverage.
            </motion.p>
          </div>
        </div>

        <div className="press-wrap">
          {/* Available Assets */}
          <section className="press-sec">
            <div className="press-sh">
              <div className="press-sh-left">
                <div className="press-sh-bar" style={{ background: 'linear-gradient(to bottom, #3b82f6, transparent)' }} />
                <span className="press-sh-label" style={{ color: '#3b82f6' }}>Available Assets</span>
              </div>
            </div>

            {/* Slideshow */}
            <PortraitSlideshow />

            {/* Assets Grid */}
            <div className="press-assets-grid">
              {assets.map((asset, index) => (
                <motion.div
                  key={index}
                  className="press-asset-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="press-asset-icon">
                    <asset.icon size={24} color="#3b82f6" />
                  </div>
                  <div className="press-asset-content">
                    <h3 className="press-asset-title">{asset.title}</h3>
                    <p className="press-asset-subtitle">{asset.subtitle}</p>
                    <p className="press-asset-desc">{asset.description}</p>
                  </div>
                  <a href={asset.file} className="press-asset-download" download>
                    <Download size={12} />
                    Download
                  </a>
                </motion.div>
              ))}
            </div>
          </section>

          <div className="press-div" />

          {/* Quote Section */}
          <section className="press-sec-sm">
            <div className="press-quote">
              <div className="press-quote-glow" />
              <FileText className="press-quote-icon" size={36} color="#3b82f6" />
              <p className="press-quote-text">
                "Access ANIMAC media resources to tell the story of imagination, creativity, and innovation that powers our universe."
              </p>
              <p className="press-quote-author">— ANIMAC Media</p>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
